import { createContext, useContext } from 'react';
import type {
  ExecutionCallbacks,
  ProposalExecutionType,
  ExecutionRequestResult,
  ExecutionTarget,
} from './execution-state';

type ExecutionIdentity =
  | {
      executionTarget: 'proposal';
      proposalType: ProposalExecutionType;
      executionTargetId: string;
    }
  | {
      executionTarget: 'transaction';
      proposalType?: never;
      executionTargetId: string;
    };

export type StartExecutionOptions = ExecutionCallbacks &
  ExecutionIdentity & {
    request: () => Promise<ExecutionRequestResult>;
    /** Revalidates shared state after another user wins the execution race. */
    onAlreadyExecuting?: (() => void | Promise<unknown>) | undefined;
  };

interface ExecutionContextValue {
  startExecution: (options: StartExecutionOptions) => string;
  isExecutionActive: (
    executionTarget: ExecutionTarget,
    executionTargetId: string
  ) => boolean;
}

export const ExecutionContext = createContext<ExecutionContextValue | null>(
  null
);

/**
 * Gives proposal and transaction screens access to the global execution flow.
 *
 * Failing fast outside the provider prevents a request from starting without
 * the persistent tray state that must own it across screen lifecycle changes.
 */
export function useExecution(): ExecutionContextValue {
  const context = useContext(ExecutionContext);
  if (!context) {
    throw new Error('useExecution must be used within an ExecutionProvider');
  }
  return context;
}
